import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { OrderNotice } from '../../../Generated/OrderNotice.ts'
import type { IBulkResponse } from 'Framework/Repository/Response/IBulkResponse.js'
import type {
  EItemType,
  FileForProcessing,
  IContentFile,
  ICoordinates,
  IOrder,
  IOrderWithCounts,
  IOrganization,
  IOrganizationRef,
  IPrimer,
  ISamplePool,
  IUser,
  NegativeControl,
  PublicOrder,
  Sample
} from 'Model/index.js'
import { isNegativeControl } from 'Model/index.js'
import { isSample } from 'Model/index.js'
import type { IFileUploadLocation } from 'Model/File/IFileUploadLocation.js'
import type { IPostprocessingInformation } from '../../../Model/Order/IPostprocessingInformation.js'
import type { IPipelineState } from '../../../Model/Order/IPipelineState.js'
import type { OrderChanged, PipelineDataChanged, PoolsChanged } from '../../Epic/Websocket/Messages.js'
import type { ILabInvite } from 'Model/Invite/IInvite.js'
import type { ISampleMetaData } from 'Model/Sample/ISampleMetaData.js'
import type { OpBatch } from '../../../View/Page/HomePage/Modals/MapToBatches.js'
import type { MergedOperation, SampleOperationResponse } from '../../../View/Page/HomePage/Modals/CSVModalTypes.js'
import type { SheetStructurePlan } from '../../../View/Page/HomePage/Modals/SheetStructurePlan.ts'
import type { SampleConfigurationTypeKey } from '../../../Generated/SampleConfigurationType.js'
import type { Taxas } from '../../../Model/ITaxa.js'
import type { SearchFilter } from '../../../Generated/SearchFilter.js'
import type { SampleMetadata } from '../../../Generated/SampleMetadata.js'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.js'
import type { OrderProcessingData } from '../../../Generated/OrderProcessingData.js'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import type { SequencingPlatformKey } from '../../../Generated/SequencingPlatform.ts'
import type { CreateOrderRequest } from '../../../Generated/CreateOrderRequest.ts'
import type { OrderArtifact } from '../../../Model/Order/OrderArtifact.ts'
import { removeRequest, removeRequestByType } from 'Flux/FluxUtils.ts'
import { trackOrderLifecycle, trackOrderEdited } from 'Services/Telemetry/index.ts'
import type { ReportRequest } from '../../../Generated/ReportRequest.js'
import type { OrderIntermediatesStatus } from '../../../Generated/OrderIntermediatesStatus.ts'
import type { ReprocessingGrantStatus } from '../../../Generated/ReprocessingGrantStatus.ts'

export enum EPrimerSearchStatus {
  IN_PROGRESS,
  NOT_FOUND,
  SUCCESS
}

export type UpdateOrderRequest = {
  orderId: string
  version: string
  name: string
  description?: string | null
  taxa: Taxas
  targetRegion?: string
  allowSingletons: boolean
  processingType: ProcessingTypeKey
  redlistMode: RedlistModeKey
  sampleConfiguration?: SampleConfigurationTypeKey
}

export type OverrideSequencingPlatformRequest = {
  orderId: string
  version: string
  platform: SequencingPlatformKey | null
}

export interface IOrderState {
  openRequests: string[]
  order?: IOrder | 'LOADING'
  /** The order whose fetch answered 404: it does not exist, or the user may not see it. */
  notFoundOrderId?: string
  sampleMetaDatas: ISampleMetaData[]
  primer?: IPrimer
  primerNotFound: EPrimerSearchStatus
  samplePools: ISamplePool[] | 'LOADING' //this is a duplicate of the dataset{} field, remove/replace on of them
  /** The order `samplePools` were fetched for. */
  samplePoolsOrderId?: string
  orders: IOrderWithCounts[]
  total: number
  postprocessingData: IPostprocessingInformation | null
  otuProcessingData: OrderProcessingData | null
  /** the otu-processing-data request has answered, with data or (no report yet) null */
  otuProcessingDataSettled: boolean
  pipelineState: IPipelineState | null
  intermediatesStatus: OrderIntermediatesStatus | null
  notices: { orderId: string; items: OrderNotice[] } | null
  reprocessingGrant: ReprocessingGrantStatus | null
  organizations: Record<string, IOrganizationRef>
  users: Record<string, IUser>
  otuCount: number | null
  fileList?: { file: IContentFile; samples?: FileForProcessing[] }[]
  fileListError?: boolean
}

const initialState: IOrderState = {
  postprocessingData: null,
  otuProcessingData: null,
  otuProcessingDataSettled: false,
  pipelineState: null,
  intermediatesStatus: null,
  notices: null,
  reprocessingGrant: null,
  openRequests: [],
  sampleMetaDatas: [],
  primerNotFound: EPrimerSearchStatus.IN_PROGRESS,
  samplePools: 'LOADING',
  orders: [],
  total: 0,
  organizations: {},
  users: {},
  otuCount: null
}

const reducers = {
  updatePrimersForSubmitRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      order: IOrder
      primer: IPrimer | 'SKIPPED'
      data: (Sample | NegativeControl)[]
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  updatePrimersForSubmitSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; result: (Sample | NegativeControl)[] }>
  ) => {
    state.openRequests = removeRequest(state, action)

    if (state.samplePools === 'LOADING') return // samplePools are not loaded yet

    const poolMap = state.samplePools.reduce(
      (acc, pool) => {
        acc[pool.id] = pool
        return acc
      },
      {} as Record<string, ISamplePool>
    )

    action.payload.result.forEach((sample) => {
      const poolToEdit = sample.poolId === undefined ? undefined : poolMap[sample.poolId]
      if (poolToEdit !== undefined) {
        if (sample.itemType === 'SAMPLE') {
          if (!updateItemInArray(poolToEdit.samples, sample)) {
            poolToEdit.samples = [...poolToEdit.samples, sample]
          }
        } else {
          if (!updateItemInArray(poolToEdit.negativeControls, sample)) {
            poolToEdit.negativeControls = [...poolToEdit.negativeControls, sample]
          }
        }
      }
    })
  },
  updatePrimersForSubmitFailure: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  loadFiles: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.fileList = undefined
    state.fileListError = false
    state.openRequests.push(action.type)
  },
  loadFilesSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; fileList: { file: IContentFile; samples?: FileForProcessing[] }[] }>
  ) => {
    state.openRequests = removeRequest(state, action)
    state.fileList = action.payload.fileList
    state.fileListError = false
  },
  loadFilesError: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; error: Error }>) => {
    state.openRequests = removeRequest(state, action)
    state.fileListError = true
  },
  batchUpdateSamples: (state: Draft<IOrderState>, action: PayloadAction<OpBatch>) => {
    state.openRequests.push(action.type)
  },
  batchUpdateSamplesSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      batch: OpBatch
      result: SampleOperationResponse
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
    const targetPoolId = action.payload.batch.poolId

    /*
     * Metadata is order-scoped, so it lands whether or not the batch names a pool this state knows.
     * It used to be applied only after finding the pool, and the metadata tab edits through a MOCK
     * pool (`id: 'metadata'`, built from the metadata rows themselves) which is not in `samplePools` —
     * so the server accepted the edit, this returned early, and the table went on showing the old
     * value. Clicking appeared to do nothing at all.
     */
    /* a create for a sample that already has metadata comes back as that existing row */
    action.payload.result.newMetadata.forEach((x) => {
      if (!updateItemInArray(state.sampleMetaDatas, x)) {
        state.sampleMetaDatas.push(x)
      }
    })
    action.payload.result.updatedMetadata.forEach((x) => updateItemInArray(state.sampleMetaDatas, x))

    if (state.samplePools === 'LOADING') return // samplePools are not loaded yet

    const poolToEdit = state.samplePools.find((x) => x.id === targetPoolId)
    if (poolToEdit === undefined) return // a mock or foreign pool: the metadata above is still applied

    /* adjust all updated samples in their respective pools */
    if (targetPoolId && action.payload.result.samples.length > 0) {
      action.payload.result.samples.forEach((sample) => {
        /* an import may have converted the item, so it can sit in the other list */
        if (sample.itemType === 'SAMPLE') {
          poolToEdit.negativeControls = poolToEdit.negativeControls.filter((x) => x.id !== sample.id)
          if (!updateItemInArray(poolToEdit.samples, sample)) {
            poolToEdit.samples = [...poolToEdit.samples, sample]
          }
          const restored = sample.sampleMetadata
          if (restored && !updateItemInArray(state.sampleMetaDatas, restored)) {
            state.sampleMetaDatas.push(restored)
          }
        } else {
          const wasSample = poolToEdit.samples.some((x) => x.id === sample.id)
          poolToEdit.samples = poolToEdit.samples.filter((x) => x.id !== sample.id)
          if (!updateItemInArray(poolToEdit.negativeControls, sample)) {
            poolToEdit.negativeControls = [...poolToEdit.negativeControls, sample]
          }
          if (wasSample) {
            state.sampleMetaDatas = state.sampleMetaDatas.filter((x) => x.sampleId !== sample.id)
          }
        }
      })
    }

    /* carry the metadata onto the pool item that holds it — negative controls included, which the
       samples-only lookup used to miss, leaving an edited NC showing its old values */
    const allMetas = [...action.payload.result.newMetadata, ...action.payload.result.updatedMetadata]
    allMetas.forEach((meta) => {
      if (!meta.sampleId) return
      const apply = (item: Sample | NegativeControl) => ({
        ...item,
        customerSampleName: meta.customerSampleName,
        sampleMetadata: meta
      })
      if (!modifyItemInArray(poolToEdit.samples, meta.sampleId, apply)) {
        modifyItemInArray(poolToEdit.negativeControls, meta.sampleId, apply)
      }
    })
  },
  batchUpdateSampleFailure: (state: Draft<IOrderState>, action: PayloadAction<Error>) => {
    state.openRequests = removeRequest(state, action)
  },
  wsOrderChanged: (state: Draft<IOrderState>, action: PayloadAction<OrderChanged>) => {
    if (!state.order) {
      console.warn('WS: Order ist null, discarding')
      return
    }
    if (state.order != 'LOADING' && state.order.id === action.payload.orderId) {
      state.order = action.payload.order
    } else {
      console.warn('Received ws message for incorrect order id, discarding')
    }
  },
  wsItemChanged: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ item: Sample | NegativeControl; orderId: string; poolId: string }>
  ) => {
    if (!state.order) {
      console.warn('WS: Order ist null, discarding')
      return
    }
    if (state.order != 'LOADING' && state.order.id !== action.payload.orderId) {
      console.warn('Received ws message for incorrect order id, discarding')
      return
    }
    if (state.samplePools === 'LOADING') {
      console.warn('Received ws message with unloaded datasets, discarding')
      return
    }
    const poolToEdit = state.samplePools[state.samplePools.findIndex((x) => x.id == action.payload.poolId)]
    if (!poolToEdit) {
      console.warn('Received ws message for unknown pool id, discarding')
      return
    }
    if (action.payload.item.itemType === 'SAMPLE') {
      poolToEdit.samples[poolToEdit.samples.findIndex((x) => x.id === action.payload.item.id)] = action.payload.item
    } else {
      poolToEdit.negativeControls[poolToEdit.negativeControls.findIndex((x) => x.id === action.payload.item.id)] = action.payload.item
    }
  },
  wsPipelineDataChanged: (state: Draft<IOrderState>, action: PayloadAction<PipelineDataChanged>) => {
    if (!state.order) {
      console.warn('WS: Order ist null, discarding')
      return
    }
    if (state.order != 'LOADING' && state.order.id !== action.payload.orderId) {
      console.warn('Received ws message for incorrect order id, discarding')
      return
    }
    state.postprocessingData = action.payload.pipelineData
  },
  wsPoolsChanged: (state: Draft<IOrderState>, action: PayloadAction<PoolsChanged>) => {
    if (!state.order) {
      console.warn('WS: Order ist null, discarding')
      return
    }

    if (state.order != 'LOADING' && state.order.id !== action.payload.orderId) {
      console.warn('Received ws message for incorrect order id, discarding')
      return
    }
    state.samplePools = action.payload.pools || []
    state.samplePoolsOrderId = action.payload.orderId
  },
  setUser: (state: Draft<IOrderState>, action: PayloadAction<{ id: string; user: IUser }>) => {
    state.users = { ...state.users, [action.payload.id]: action.payload.user }
  },
  setOrganization: (state: Draft<IOrderState>, action: PayloadAction<{ id: string; organization: IOrganization }>) => {
    state.organizations = { ...state.organizations, [action.payload.id]: action.payload.organization }
  },
  fetchPipelineState: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  fetchPipelineStateResult: (state: Draft<IOrderState>, action: PayloadAction<{ result: IPipelineState | null }>) => {
    state.openRequests = removeRequest(state, action)
    state.pipelineState = action.payload.result
  },
  fetchIntermediatesStatus: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  fetchIntermediatesStatusResult: (state: Draft<IOrderState>, action: PayloadAction<{ result: OrderIntermediatesStatus | null }>) => {
    state.openRequests = removeRequest(state, action)
    state.intermediatesStatus = action.payload.result
  },
  // Not an open request: notices must never hold up the page's loading state.
  fetchOrderNotices: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    if (state.notices?.orderId !== action.payload.orderId) {
      state.notices = null
    }
  },
  fetchOrderNoticesResult: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; items: OrderNotice[] }>) => {
    state.notices = action.payload
  },
  fetchReprocessingGrant: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  fetchReprocessingGrantResult: (state: Draft<IOrderState>, action: PayloadAction<{ result: ReprocessingGrantStatus | null }>) => {
    state.openRequests = removeRequest(state, action)
    state.reprocessingGrant = action.payload.result
  },
  rerunPreprocessingRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  rerunPreprocessingSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
    state.reprocessingGrant = null
  },
  rerunPreprocessingFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  fetchPostprocessingData: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  fetchPostprocessingDataResult: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      result: IPostprocessingInformation | null
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
    state.postprocessingData = action.payload.result
  },
  fetchOtuProcessingData: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.otuProcessingDataSettled = false
    state.openRequests.push(action.type)
  },
  fetchOtuProcessingDataResult: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      result: OrderProcessingData | null
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
    state.otuProcessingData = action.payload.result
    state.otuProcessingDataSettled = true
  },
  resetOrder: (state: Draft<IOrderState>) => {
    state.order = 'LOADING'
    state.samplePools = []
    state.samplePoolsOrderId = undefined
    state.postprocessingData = null
    state.otuProcessingData = null
    state.otuProcessingDataSettled = false
  },
  updateOrder: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.order = action.payload
  },
  getPublicOrderRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; token: string }>) => {
    state.order = 'LOADING'
    state.openRequests.push(action.type)
  },
  getPublicOrderSuccess: (state: Draft<IOrderState>, action: PayloadAction<PublicOrder>) => {
    state.order = action.payload.order
    if (action.payload.lab) {
      state.organizations = { ...state.organizations, [action.payload.lab.id]: action.payload.lab }
    }
    if (action.payload.customer) {
      state.organizations = { ...state.organizations, [action.payload.customer.id]: action.payload.customer }
    }
    state.samplePools = action.payload.pools || []
    state.samplePoolsOrderId = action.payload.order.id
    const newDataset: Record<string, ISamplePool> = {}
    if (action.payload.pools) {
      action.payload.pools.forEach((sp) => {
        newDataset[sp.id] = sp
      })
    }
    state.otuCount = action.payload.otuCount || null
    state.pipelineState = action.payload.pipelineState || null
    state.openRequests = removeRequest(state, action)
  },
  getPublicOrderFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  getOrderRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.order = 'LOADING'
    state.notFoundOrderId = undefined
    state.postprocessingData = null
    state.otuProcessingData = null
    state.otuProcessingDataSettled = false
    state.openRequests.push(action.type)
  },
  getOrderRequestSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    console.log('got order successfully', action.payload, state.order)
    //state = {...initialState}
    state.order = action.payload
    state.openRequests = removeRequest(state, action)
  },
  getOrderRequestFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error; orderId: string; notFound: boolean }>) => {
    console.error(action.payload.error)
    if (action.payload.notFound) {
      state.notFoundOrderId = action.payload.orderId
    }
    state.openRequests = removeRequest(state, action)
  },
  createOrderRequest: (state: Draft<IOrderState>, action: PayloadAction<Omit<CreateOrderRequest, 'type'>>) => {
    state.openRequests.push(action.type)
  },
  createOrderSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
    trackOrderLifecycle('created', action.payload)
  },
  createOrderFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  createOrderDraftRequest: (state: Draft<IOrderState>, action: PayloadAction<Omit<CreateOrderRequest, 'type'>>) => {
    state.openRequests.push(action.type)
  },
  createOrderDraftSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
    trackOrderLifecycle('draft_created', action.payload)
  },
  createOrderDraftFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  inviteLabRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      invite: { labEmail?: string; organizationId?: string; additionalText: string }
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  inviteLabRequestSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; invite: ILabInvite }>) => {
    state.openRequests = removeRequest(state, action)
  },
  inviteLabRequestFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  createSamplepoolRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string | undefined }>) => {
    state.openRequests.push(action.type)
  },
  createSamplepoolSuccess: (state: Draft<IOrderState>, action: PayloadAction<ISamplePool>) => {
    state.openRequests = removeRequest(state, action)
    if (state.samplePools === 'LOADING') {
      console.warn('datasets where not yet loaded, results may be bad')
      state.samplePools = [action.payload]
    } else {
      state.samplePools.push(action.payload) //SVSTAMM: I added this without really understanding the implications
    }
  },
  createSamplePoolFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  requestFileUploadURL: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      poolId: string
      files: {
        fileName: string
        encodedMd5: string
        contentType: 'SAMPLE'
      }[]
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  requestFileUploadURLSuccess: (state: Draft<IOrderState>, action: PayloadAction<IFileUploadLocation[]>) => {
    state.openRequests = removeRequest(state, action)
    console.log('request File Upload URL Success!')
    console.log(action)
  },
  requestFileUploadURLFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  postContentRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      poolId: string
      sample: Omit<Sample, 'id'> | Omit<NegativeControl, 'id'>
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  postSampleRequestSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; content: Sample | NegativeControl }>
  ) => {
    state.openRequests = removeRequest(state, action)
    if (state.samplePools === 'LOADING') {
      console.error('Unexpected state, pools should be loaded')
      throw Error('unexpected state, pools should be loaded')
    }
    const spIndex = state.samplePools.findIndex((sp) => sp.id === action.payload.poolId)
    if (spIndex === -1) {
      console.warn('Unexpected state, maybe user navigated to a different order?')
      return
    }
    const sp = state.samplePools[spIndex]
    // Upsert, not push: creating a sample is idempotent server-side (re-uploading a file that already
    // belongs to a sample returns that sample instead of a duplicate), so the same id can come back
    // twice and must not show up twice in the pool.
    const upsert = <T extends { id: string }>(list: Draft<T>[], item: T) => {
      const index = list.findIndex((x) => x.id === item.id)
      if (index === -1) {
        list.push(item as Draft<T>)
      } else {
        list[index] = item as Draft<T>
      }
    }
    if (isSample(action.payload.content)) {
      upsert(sp.samples, action.payload.content)
    } else {
      upsert(sp.negativeControls, action.payload.content)
    }
    state.samplePools = [...state.samplePools.slice(0, spIndex), sp, ...state.samplePools.slice(spIndex + 1)]
  },
  getSampleMetaDatasRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  getSampleMetaDatasSuccess: (state: Draft<IOrderState>, action: PayloadAction<ISampleMetaData[]>) => {
    state.openRequests = removeRequest(state, action)
    state.sampleMetaDatas = action.payload
  },
  getSampleMetaDatasFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  uploadSampleMetaDataRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; sampleMetaDatas: ISampleMetaData[] }>
  ) => {
    state.openRequests.push(action.type)
  },
  uploadSampleMetaDataSuccess: (state: Draft<IOrderState>, action: PayloadAction<ISampleMetaData[]>) => {
    state.openRequests = removeRequest(state, action)
    state.sampleMetaDatas = action.payload
  },
  uploadSampleMetaDataFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  createPrimerAddRequest: (state: Draft<IOrderState>, action: PayloadAction<IPrimer>) => {
    state.openRequests.push(action.type)
  },
  createPrimerAddSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ resource: IPrimer }>) => {
    state.openRequests = removeRequest(state, action)
    state.primerNotFound = EPrimerSearchStatus.SUCCESS
  },
  createPrimerAddFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  updateContentRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      samplePoolId: string
      data: {
        itemType: EItemType
        forwardFile?: IContentFile
        forwardFileId: string
        forwardPrimer?: string
        reverseFile?: IContentFile
        reverseFileId?: string
        reversePrimer?: string
        customerSampleName?: string
        primerLiterature?: string
        locality?: string
        materialType?: string
        sampleCoordinates?: ICoordinates
      }[]
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  updateContentSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; content: (Sample | NegativeControl)[] }>
  ) => {
    state.openRequests = removeRequest(state, action)
    if (state.samplePools === 'LOADING') {
      console.error('Unexpected state, pools should be loaded')
      throw Error('unexpected state, pools should be loaded')
    }
    const poolToEdit = state.samplePools.find((pool) => pool.id === action.payload.poolId)
    if (poolToEdit === undefined) return // the specified pool was not found

    action.payload.content.forEach((item) => {
      if (item.itemType === 'SAMPLE') {
        updateItemInArray(poolToEdit.samples, item)
      } else {
        updateItemInArray(poolToEdit.negativeControls, item)
      }
    })
  },
  updateContentFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  applySheetImportRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; plan: SheetStructurePlan; ops: MergedOperation[] }>
  ) => {
    state.openRequests.push(action.type)
  },
  applySheetImportSuccess: (state: Draft<IOrderState>, action: PayloadAction<void>) => {
    state.openRequests = removeRequest(state, action)
  },
  applySheetImportFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not apply the sheet import', action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  movePoolItemsRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      sourcePoolId: string
      targetPoolId: string
      items: string[]
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  movePoolItemsRequestSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      sourcePoolId: string
      targetPoolId: string
      items: FileForProcessing[]
      itemIds: string[]
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
    if (state.samplePools === 'LOADING') {
      console.error('Unexpected state, pools should be loaded')
      throw Error('unexpected state, pools should be loaded')
    }
    const sourcePool = state.samplePools.find((pool) => pool.id === action.payload.sourcePoolId)
    const targetPool = state.samplePools.find((pool) => pool.id === action.payload.targetPoolId)
    if (!sourcePool || !targetPool) {
      throw Error('Could not find source or target pool')
    }

    // remove from old pools
    sourcePool.negativeControls = sourcePool.negativeControls.filter((x) => !action.payload.itemIds.includes(x.id))
    sourcePool.samples = sourcePool.samples.filter((x) => !action.payload.itemIds.includes(x.id))

    // add to new pools
    const moved = action.payload.items.map((x) => ({ ...x, poolLabel: targetPool.label }))
    targetPool.negativeControls = [...targetPool.negativeControls, ...moved.filter((x) => isNegativeControl(x))]
    targetPool.samples = [...targetPool.samples, ...moved.filter((x) => isSample(x))]
  },
  movePoolItemsRequestFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not move sample', action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  convertPoolItemRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; item: FileForProcessing }>
  ) => {
    state.openRequests.push(action.type)
  },
  convertPoolItemRequestSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; item: FileForProcessing }>
  ) => {
    if (state.samplePools === 'LOADING') {
      console.error('Unexpected state, pools should be loaded')
      throw Error('unexpected state, pools should be loaded')
    }
    const pool = state.samplePools.find((pool) => pool.id === action.payload.poolId)
    if (!pool) {
      console.error('Unexpected state, could not find pool')
      throw Error('unexpected state, could not find pool')
    }
    pool.samples = [...pool.samples.filter((x) => x.id !== action.payload.item.id)]
    pool.negativeControls = [...pool.negativeControls.filter((x) => x.id !== action.payload.item.id)]

    if (isNegativeControl(action.payload.item)) {
      pool.negativeControls = [...pool.negativeControls, { ...action.payload.item, customerSampleName: undefined }]
      // Metadata is no longer deleted on conversion (it is shadowed and restored on convert-back),
      // but we drop it from the local store so it is hidden while the item is an NC.
      state.sampleMetaDatas = state.sampleMetaDatas.filter((x) => x.sampleId !== action.payload.item.id)
    } else {
      if (!isSample(action.payload.item)) {
        throw Error('Negative controls cannot be added to sample pools - unexpected state')
      } else {
        pool.samples = [...pool.samples, action.payload.item]
        // Converting back to SAMPLE: the backend re-surfaces the shadowed metadata on the item.
        // Re-add it to the local store so the metadata view is consistent without a refetch.
        const restored = action.payload.item.sampleMetadata
        if (restored) {
          state.sampleMetaDatas = [...state.sampleMetaDatas.filter((x) => x.id !== restored.id), restored]
        }
      }
    }
    state.openRequests = removeRequest(state, action)
  },
  convertPoolItemRequestFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  deletePoolItemRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; itemId: string; version: string }>
  ) => {
    state.openRequests.push(action.type)
  },
  deletePoolItemSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; poolId: string; itemId: string }>) => {
    if (state.samplePools === 'LOADING') {
      console.error('Unexpected state, pools should be loaded')
      throw Error('unexpected state, pools should be loaded')
    }
    state.samplePools
      .filter((x) => x.id === action.payload.poolId)
      .forEach((pool) => {
        pool.samples = pool.samples.filter((x) => x.id !== action.payload.itemId)
        pool.negativeControls = pool.negativeControls.filter((x) => x.id !== action.payload.itemId)
      })
    state.openRequests = removeRequest(state, action)
  },
  deletePoolItemFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not delete sample', action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  attachReverseFileRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; sampleId: string; reverseFileId: string; absorbSampleId?: string }>
  ) => {
    state.openRequests.push(action.type)
  },
  attachReverseFileSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; content: Sample | NegativeControl; absorbedSampleId?: string }>
  ) => {
    state.openRequests = removeRequest(state, action)
    if (state.samplePools === 'LOADING') {
      return
    }
    const { content, absorbedSampleId } = action.payload
    state.samplePools.forEach((pool) => {
      pool.samples = pool.samples
        .filter((item) => item.id !== absorbedSampleId)
        .map((item) => (item.id === content.id && isSample(content) ? content : item))
      pool.negativeControls = pool.negativeControls
        .filter((item) => item.id !== absorbedSampleId)
        .map((item) => (item.id === content.id && isNegativeControl(content) ? content : item))
    })
  },
  attachReverseFileFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: unknown }>) => {
    state.openRequests = removeRequest(state, action)
  },
  deleteSamplePoolRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; poolId: string }>) => {
    state.openRequests.push(action.type)
  },
  deleteSamplePoolSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; poolId: string }>) => {
    if (state.samplePools === 'LOADING') {
      console.error('Unexpected state, pools should be loaded')
      throw Error('unexpected state, pools should be loaded')
    }
    // Remove the pool from the samplePools array
    state.samplePools = state.samplePools.filter((pool) => pool.id !== action.payload.poolId)

    state.openRequests = removeRequest(state, action)
  },
  updateSamplePoolLabelRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; label?: string; version: string }>
  ) => {
    state.openRequests.push(action.type)
  },
  updateSamplePoolLabelSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; label?: string; version?: string }>
  ) => {
    if (state.samplePools !== 'LOADING') {
      const pool = state.samplePools.find((p) => p.id === action.payload.poolId)
      if (pool) {
        pool.label = action.payload.label
        pool.version = action.payload.version
        pool.samples.forEach((s) => (s.poolLabel = action.payload.label))
        pool.negativeControls.forEach((nc) => (nc.poolLabel = action.payload.label))
      }
    }
    state.openRequests = removeRequest(state, action)
  },
  updateSamplePoolLabelFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not rename sample pool', action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  deleteSamplePoolFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not delete sample pool', action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  unlinkMetadatasRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; metadatas: SampleMetadata[] }>) => {
    state.openRequests.push(action.type)
  },
  unlinkMetadatasSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; unlinkedMetadatas: SampleMetadata[] }>) => {
    state.openRequests = removeRequest(state, action)
    state.sampleMetaDatas = [
      ...state.sampleMetaDatas.filter((x) => !action.payload.unlinkedMetadatas.map((y) => y.id).includes(x.id)),
      ...action.payload.unlinkedMetadatas
    ]
    // Unlinking detaches the metadata from its sample, so the embedded copy on that sample (which drives
    // the pool tab's name + "merged" badge) must be cleared too — mirroring what a refetch would return.
    detachMetadataFromSamples(
      state,
      action.payload.unlinkedMetadatas.map((x) => x.id)
    )
  },
  unlinkMetadatasFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not unlink sample metadata', action.payload.error)
  },
  deleteMetadataItemRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; metadataId: string }>) => {
    state.openRequests.push(action.type)
  },
  deleteMetadataItemSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; metadataId: string }>) => {
    state.openRequests = removeRequest(state, action)
    state.sampleMetaDatas = state.sampleMetaDatas.filter((x) => x.id !== action.payload.metadataId)
    // The deleted metadata is also embedded on whichever sample it was merged to; drop it there so the
    // pool tab stops showing the now-gone name/merge status (the metadata-derived name would be empty
    // on a refetch anyway).
    detachMetadataFromSamples(state, [action.payload.metadataId])
  },
  deleteMetadataItemFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    console.error('Could not delete sample metadata', action.payload.error)
  },
  updatePoolItemRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; poolId: string; item: FileForProcessing }>
  ) => {
    state.openRequests.push(action.type)
  },
  claimOrderRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{
      orderId: string
      token?: string
      targetTaxa?: string
      targetRegion?: string
      allowSingletons?: boolean
      sampleConfiguration?: SampleConfigurationTypeKey
      redlistMode?: RedlistModeKey
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  claimOrderRequestSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    console.log(
      'claiming success, prev version',
      state.order !== 'LOADING' ? state.order?.version : 'loading???',
      'next state',
      action.payload.version
    )
    state.order = action.payload
    trackOrderLifecycle('claimed', action.payload)
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  claimOrderFailure: (state: Draft<IOrderState>, _action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequestByType(state, orderSlice.actions.claimOrderRequest.type)
  },
  submitOrderRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  submitOrderSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
    trackOrderLifecycle('submitted', action.payload)
  },
  submitOrderFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: string; details?: string[] }>) => {
    state.openRequests = removeRequest(state, action)
    console.error('Submit order failed with error ', action.payload.error)
  },
  submitPostprocessingRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; excludedItems: string[] }>) => {
    state.openRequests.push(action.type)
  },
  submitPostprocessingSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
    trackOrderLifecycle('postprocessing_started', action.payload)
  },
  submitPostprocessingFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: string; details?: string[] }>) => {
    state.openRequests = removeRequest(state, action)
    console.error('Submit postprocessing failed with error ', action.payload.error)
  },
  pullbackOrderRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  pullbackOrderSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
  },
  getAllDatasetRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  getAllDatasetSuccess: {
    reducer: (state: Draft<IOrderState>, action: PayloadAction<ISamplePool[], string, { orderId?: string }>) => {
      state.samplePools = action.payload || []
      state.samplePoolsOrderId = action.meta.orderId
      state.openRequests = removeRequest(state, action)
    },
    prepare: (pools: ISamplePool[], orderId?: string) => ({ payload: pools, meta: { orderId } })
  },
  getAllDatasetFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  deleteOrderRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  deleteOrderFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  deleteOrderSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.order = action.payload
    state.openRequests = removeRequest(state, action)
    trackOrderLifecycle('deleted', action.payload)
  },
  getAllOrderRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<
      | {
          size: number
          page: number
          filters: SearchFilter[]
        }
      | undefined
    >
  ) => {
    state.openRequests.push(action.type)
  },
  getAllOrderSuccess: (state: Draft<IOrderState>, action: PayloadAction<IBulkResponse<IOrderWithCounts>>) => {
    state.orders = action.payload.content
    state.total = action.payload.totalSize
    state.openRequests = removeRequest(state, action)
  },
  getAllOrderFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  getFastqRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  getFastqRequestSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; link: string }>) => {
    state.openRequests = removeRequest(state, action)
  },
  getFastqRequestFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  getReportRequest: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderName: string; orderId: string; checksum?: string; settings?: ReportRequest }>
  ) => {
    state.openRequests.push(action.type)
  },
  getReportRequestSuccess: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; link: string }>) => {
    state.openRequests = removeRequest(state, action)
  },
  getReportRequestFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  downloadOrderArtifactRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string; artifact: OrderArtifact }>) => {
    state.openRequests.push(action.type)
  },
  downloadOrderArtifactSuccess: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; artifact: OrderArtifact; link: string }>
  ) => {
    state.openRequests = removeRequest(state, action)
  },
  downloadOrderArtifactFailure: (
    state: Draft<IOrderState>,
    action: PayloadAction<{ orderId: string; artifact: OrderArtifact; error: Error }>
  ) => {
    state.openRequests = removeRequest(state, action)
  },
  getDatasetReportRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDatasetReportSuccess: (state: Draft<IOrderState>, action: PayloadAction<any>) => {
    state.openRequests = removeRequest(state, action)
  },
  getDatasetReportFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  getOtuCountRequest: (state: Draft<IOrderState>, action: PayloadAction<{ orderId: string }>) => {
    state.openRequests.push(action.type)
    state.otuCount = null
  },
  getOtuCountSuccess: (state: Draft<IOrderState>, action: PayloadAction<number>) => {
    state.openRequests = removeRequest(state, action)
    state.otuCount = action.payload
  },
  getOtuCountFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
    state.otuCount = -1
  },
  updateOrderNameRequest: (state: Draft<IOrderState>, action: PayloadAction<UpdateOrderRequest>) => {
    state.openRequests.push(action.type)
  },
  updateOrderNameSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    const previous = state.order
    if (previous && previous !== 'LOADING') {
      trackOrderEdited(previous, action.payload)
    }
    state.order = action.payload
    console.info('success, version is', action.payload.version)
  },
  updateOrderNameFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  overrideSequencingPlatformRequest: (state: Draft<IOrderState>, action: PayloadAction<OverrideSequencingPlatformRequest>) => {
    state.openRequests.push(action.type)
  },
  overrideSequencingPlatformSuccess: (state: Draft<IOrderState>, action: PayloadAction<IOrder>) => {
    state.openRequests = removeRequest(state, action)
    state.order = action.payload
  },
  overrideSequencingPlatformFailure: (state: Draft<IOrderState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  reset: () => {
    return initialState
  },
  /* used to load a state for demonstration purposes */
  applyState: (state: Draft<IOrderState>, action: PayloadAction<IOrderState>) => {
    return { ...state, ...action.payload }
  }
}

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers
})

const updateItemInArray = <T extends { id: string }>(array: T[], item: T) => {
  const index = array.findIndex((x) => x.id === item.id)
  if (index !== -1) {
    array[index] = item
    return true
  }
  return false
}

/** Returns whether the id was found, so a caller can fall through to another array. */
const modifyItemInArray = <T extends { id: string }>(array: T[], id: string, modifier: (item: T) => T): boolean => {
  const index = array.findIndex((x) => x.id === id)
  if (index === -1) return false
  array[index] = modifier(array[index])
  return true
}

/**
 * Detach the given metadata (by metadata id) from any pool sample/NC it was merged onto. Pool items carry
 * an embedded `sampleMetadata` plus a metadata-derived `customerSampleName` (kept in sync by
 * batchUpdateSamplesSuccess), so deleting/unlinking metadata must also clear it here — otherwise the pool
 * tabs keep rendering the stale name and "merged" badge. Matches by metadata id because unlink clears the
 * metadata's sampleId before this runs.
 */
const detachMetadataFromSamples = (state: Draft<IOrderState>, metadataIds: string[]) => {
  if (state.samplePools === 'LOADING') return
  const ids = new Set(metadataIds)
  state.samplePools.forEach((pool) => {
    ;[...pool.samples, ...pool.negativeControls].forEach((item) => {
      if (item.sampleMetadata && ids.has(item.sampleMetadata.id)) {
        item.sampleMetadata = undefined
        item.customerSampleName = undefined
      }
    })
  })
}
